import { BaseApi } from "@/api/base";
import type { reAssignTruckRequest, RouteStopDto, TripDto, TripRouteDto } from "@/api/trips/dto/trip.dto";
import type { Response } from "@/api/client";
import type { FileDto } from "@/api/files/dto/file.dto";
import type { CheckCallDTO, CheckCallResponse } from "@/api/trips/dto/check-call";
import type { TripUpdateResponse } from "@/api/trips/dto/update";
import type { TripMonitorResponse } from "@/api/trips/dto/trip-monitor";

export class TripsApi extends BaseApi {
  getTripById(tripId: number): Promise<Response<TripDto>> {
    return this.client.get("/trips/" + tripId);
  }

  upsertTrip(trip: any): Promise<Response<TripDto>> {
    return this.client.save("/trips", trip);
  }

  getRouteByTripId(tripId: number, routeId: number): Promise<Response<TripRouteDto>> {
    return this.client.get("/trips/" + tripId + "/routes/" + routeId);
  }

  confirmRouteDispatch(tripId: string | number, routeId: string | number, data: any): Promise<Response<CheckCallDTO>> {
    return this.client.post("/trips/" + tripId + "/routes/" + routeId + "/confirm-dispatch", data);
  }

  confirmRouteRedispatch(tripId: string | number, routeId: string | number): Promise<Response<TripRouteDto>> {
    return this.client.post("/trips/" + tripId + "/routes/" + routeId + "/confirm-redispatch", {});
  }

  checkInRouteStop(tripId: number, routeStopId: number, data: any): Promise<Response<RouteStopDto>> {
    return this.client.post("/trips/" + tripId + "/route-stops/" + routeStopId + "/check-in", data);
  }

  getRouteStop(tripId: string | number, routeStopId: string): Promise<Response<RouteStopDto>> {
    return this.client.get("/trips/" + tripId + "/route-stops/" + routeStopId);
  }

  loadRouteStop(tripId: string | number, routeStopId: string, data: any): Promise<Response<RouteStopDto>> {
    return this.client.post("/trips/" + tripId + "/route-stops/" + routeStopId + "/load", data);
  }

  uploadRouteStopFile(
    tripId: string | number,
    routeStopId: string,
    fileType: string,
    file: File,
    bol?: string,
  ): Promise<Response<FileDto>> {
    const data = new FormData();
    data.append("file", file);
    if (bol) {
      data.append("bol", bol);
    }

    return this.client.post("/trips/" + tripId + "/route-stops/" + routeStopId + "/files/" + fileType, data, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  }

  unloadRouteStop(tripId: string | number, routeStopId: string, data: any): Promise<Response<RouteStopDto>> {
    return this.client.post("/trips/" + tripId + "/route-stops/" + routeStopId + "/unload", data);
  }

  checkOutRouteStop(tripId: string | number, routeStopId: string | number, data: any): Promise<Response<RouteStopDto>> {
    return this.client.post("/trips/" + tripId + "/route-stops/" + routeStopId + "/good-to-go", data);
  }

  verifyRouteStop(tripId: string | number, routeStopId: string | number, data: any): Promise<Response<RouteStopDto>> {
    return this.client.patch("/trips/" + tripId + "/route-stops/" + routeStopId + "/verify", data);
  }

  cancelDispatchConfirmation(tripId: string | number, routeId: string | number): Promise<Response<void>> {
    return this.client.delete("/trips/" + tripId + "/routes/" + routeId + "/confirm-dispatch");
  }

  cancelCheckIn(tripId: string | number, stopId: string): Promise<Response<void>> {
    return this.client.delete("/trips/" + tripId + "/route-stops/" + stopId + "/check-in");
  }

  cancelLoad(tripId: string | number, stopId: string): Promise<Response<void>> {
    return this.client.delete("/trips/" + tripId + "/route-stops/" + stopId + "/load");
  }

  cancelLoadVerification(tripId: string | number, stopId: string): Promise<Response<void>> {
    return this.client.delete("/trips/" + tripId + "/route-stops/" + stopId + "/load-verify");
  }

  cancelUnload(tripId: string | number, stopId: string): Promise<Response<void>> {
    return this.client.delete("/trips/" + tripId + "/route-stops/" + stopId + "/unload");
  }

  cancelUnloadVerification(tripId: string | number, stopId: string): Promise<Response<void>> {
    return this.client.delete("/trips/" + tripId + "/route-stops/" + stopId + "/unload-verify");
  }

  cancelGoodToGo(tripId: string | number, stopId: string): Promise<Response<void>> {
    return this.client.delete("/trips/" + tripId + "/route-stops/" + stopId + "/good-to-go");
  }

  cancelTravelOrder(tripId: string | number, travelOrderId: string, data: any): Promise<Response<TripRouteDto>> {
    return this.client.post("/trips/" + tripId + "/travel-orders/" + travelOrderId + "/cancel", data);
  }

  getTripFiles(tripId: string | number): Promise<Response<FileDto[]>> {
    return this.client.get("/trips/" + tripId + "/files");
  }

  getCheckCalls(tripId: string | number, limit = 0): Promise<Response<CheckCallResponse>> {
    return this.client.get("/trips/" + tripId + "/check-calls?limit=" + limit);
  }

  getUpdates(tripId: string | number): Promise<Response<TripUpdateResponse>> {
    return this.client.get("/trips/" + tripId + "/updates");
  }

  fetchTripMonitor(): Promise<Response<TripMonitorResponse>> {
    return this.client.get("/trips/monitor");
  }

  updateRouteInterval(tripId: number, routeId: number, interval: number | null): Promise<Response<void>> {
    return this.client.post("/trips/" + tripId + "/routes/" + routeId + "/check-call-interval", {
      interval,
    });
  }

  updateTravelOrderInterval(tripId: number, toId: number, interval: number | null): Promise<Response<void>> {
    return this.client.post("/trips/" + tripId + "/travel-orders/" + toId + "/update-interval", {
      interval,
    });
  }

  reAssignTruckOnRoute(tripId: number, routeId: number, data: reAssignTruckRequest): Promise<Response<void>> {
    return this.client.post("/trips/" + tripId + "/routes/" + routeId + "/assign-truck", data);
  }
}
